import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { EdFiLogo, SNPLLogo } from '../components/EdFiLogo';
import { MobileMenuButton, MobileMenu } from '../components/MobileNav';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Zap, CheckCircle, XCircle, ArrowRight, Clock, DollarSign,
  User, MapPin, Calendar, Shield, Loader2, FileText, GraduationCap
} from 'lucide-react';

const STEPS_ICONS = [
  { icon: <Shield className="w-5 h-5" />, label: 'Validando CURP...' },
  { icon: <User className="w-5 h-5" />, label: 'Verificando identidad...' },
  { icon: <FileText className="w-5 h-5" />, label: 'Consultando Buro...' },
  { icon: <DollarSign className="w-5 h-5" />, label: 'Calculando monto...' },
];

const PreApprovalPage = () => {
  const [curp, setCurp] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(-1);
  const [result, setResult] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleCheck = async () => {
    if (curp.length !== 18) return;
    setLoading(true);
    setResult(null);
    setCurrentStep(0);

    // Animate through steps
    for (let i = 0; i < STEPS_ICONS.length; i++) {
      setCurrentStep(i);
      await new Promise(r => setTimeout(r, 600));
    }

    try {
      const res = await api.post('/pre-approval/check', { curp: curp.toUpperCase() });
      setResult(res.data);
    } catch (e) {
      setResult({ approved: false, stage: 'error', error: e.response?.data?.detail || 'Error de conexion' });
    }
    setLoading(false);
    setCurrentStep(-1);
  };

  const formatPrice = (p) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 }).format(p || 0);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/90 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between">
            <EdFiLogo size="md" linkTo="/" />
            <div className="hidden md:flex items-center gap-6">
              <Link to="/programs" className="text-slate-600 hover:text-brand-blue font-medium">Catalogo</Link>
              <Link to="/calculator" className="text-slate-600 hover:text-brand-blue font-medium">Calculadora</Link>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/register">
                <Button className="bg-brand-magenta hover:bg-brand-magenta-dark text-white text-sm">Registrarse</Button>
              </Link>
              <MobileMenuButton open={mobileOpen} onToggle={() => setMobileOpen(!mobileOpen)} />
            </div>
          </div>
          <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} links={[
            { to: '/programs', label: 'Catalogo' },
            { to: '/calculator', label: 'Calculadora' },
            { to: '/login', label: 'Iniciar Sesion' },
          ]} />
        </div>
      </nav>

      <main className="pt-24 sm:pt-28 pb-20">
        {/* Hero */}
        <section className="bg-brand-blue py-12 sm:py-20 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_30%_50%,_rgba(233,30,140,0.3),_transparent_60%)]"></div>
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center relative">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-magenta/20 rounded-full text-white text-sm font-medium mb-6">
              <Zap className="w-4 h-4" /> Pre-Aprobacion Express
            </div>
            <h1 className="text-3xl sm:text-5xl font-black text-white tracking-tight mb-4">
              Descubre en 30 segundos si calificas
            </h1>
            <p className="text-base sm:text-xl text-white/70 mb-10 max-w-xl mx-auto">
              Solo necesitas tu CURP. Sin registro, sin documentos, sin compromiso.
            </p>

            {/* CURP Input */}
            <div className="max-w-md mx-auto">
              <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-2xl">
                <label className="block text-left text-sm font-semibold text-slate-700 mb-2">
                  Ingresa tu CURP (18 caracteres)
                </label>
                <Input
                  value={curp}
                  onChange={e => setCurp(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 18))}
                  placeholder="AAAA000000HXXAAA00"
                  className="font-mono text-lg text-center tracking-wider h-14 mb-3 uppercase"
                  maxLength={18}
                  disabled={loading}
                  data-testid="curp-input"
                />
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs text-slate-400">{curp.length}/18 caracteres</span>
                  {curp.length === 18 && <Badge className="bg-green-100 text-green-700">Formato completo</Badge>}
                </div>
                <Button
                  onClick={handleCheck}
                  disabled={curp.length !== 18 || loading}
                  className="w-full bg-brand-magenta hover:bg-brand-magenta-dark text-white text-lg py-6"
                  data-testid="check-btn"
                >
                  {loading ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Verificando...</>
                  ) : (
                    <><Zap className="w-5 h-5 mr-2" /> Verificar Pre-Aprobacion</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Processing Steps Animation */}
        {loading && currentStep >= 0 && (
          <section className="py-10 bg-white border-b">
            <div className="max-w-md mx-auto px-4">
              <div className="space-y-4">
                {STEPS_ICONS.map((step, i) => (
                  <div key={i} className={`flex items-center gap-4 p-3 rounded-xl transition-all duration-300 ${i <= currentStep ? 'bg-brand-blue/5' : 'opacity-30'}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${i < currentStep ? 'bg-green-500 text-white' : i === currentStep ? 'bg-brand-blue text-white animate-pulse' : 'bg-slate-200 text-slate-400'}`}>
                      {i < currentStep ? <CheckCircle className="w-5 h-5" /> : step.icon}
                    </div>
                    <span className={`font-medium ${i <= currentStep ? 'text-slate-900' : 'text-slate-400'}`}>{step.label}</span>
                    {i === currentStep && <Loader2 className="w-4 h-4 ml-auto animate-spin text-brand-blue" />}
                    {i < currentStep && <CheckCircle className="w-4 h-4 ml-auto text-green-500" />}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Result */}
        {result && !loading && (
          <section className="py-10 sm:py-16">
            <div className="max-w-2xl mx-auto px-4 sm:px-6">
              {result.approved ? (
                /* APPROVED */
                <div className="text-center animate-fade-up">
                  <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-white" />
                  </div>
                  <h2 className="text-3xl sm:text-4xl font-black text-slate-900 mb-2" data-testid="approval-result">
                    Pre-Aprobado!
                  </h2>
                  <p className="text-slate-600 mb-8">Felicidades, calificas para financiamiento educativo Ed.Fi</p>

                  {/* Amount Card */}
                  <Card className="mb-8 border-2 border-green-200 bg-green-50/50">
                    <CardContent className="pt-8 pb-8">
                      <p className="text-sm text-slate-600 mb-1">Monto pre-aprobado hasta</p>
                      <p className="text-5xl sm:text-6xl font-black text-green-600 mb-2" data-testid="approved-amount">
                        {formatPrice(result.max_amount)}
                      </p>
                      <p className="text-sm text-slate-500">
                        Perfil crediticio: <Badge className="bg-brand-blue/10 text-brand-blue capitalize">{result.credit_tier}</Badge>
                      </p>
                    </CardContent>
                  </Card>

                  {/* Demographics */}
                  {result.demographics && (
                    <div className="grid grid-cols-3 gap-3 mb-8">
                      <div className="bg-white rounded-xl p-4 border text-center">
                        <Calendar className="w-5 h-5 text-brand-blue mx-auto mb-1" />
                        <p className="text-lg font-bold">{result.demographics.age} anos</p>
                        <p className="text-xs text-slate-500">Edad</p>
                      </div>
                      <div className="bg-white rounded-xl p-4 border text-center">
                        <User className="w-5 h-5 text-brand-blue mx-auto mb-1" />
                        <p className="text-lg font-bold">{result.demographics.gender}</p>
                        <p className="text-xs text-slate-500">Genero</p>
                      </div>
                      <div className="bg-white rounded-xl p-4 border text-center">
                        <MapPin className="w-5 h-5 text-brand-blue mx-auto mb-1" />
                        <p className="text-sm font-bold">{result.demographics.state}</p>
                        <p className="text-xs text-slate-500">Estado</p>
                      </div>
                    </div>
                  )}

                  {/* Payment Options */}
                  <div className="grid sm:grid-cols-2 gap-4 mb-8">
                    {result.estimated_payments && Object.values(result.estimated_payments).map(p => (
                      <Card key={p.term} className="text-left">
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between mb-3">
                            <Badge variant="outline">{p.term} meses</Badge>
                            <span className="text-xs text-slate-500">Tasa: {result.interest_rate_annual}% anual</span>
                          </div>
                          <p className="text-2xl font-bold text-brand-blue">{formatPrice(p.monthly)}<span className="text-sm text-slate-500 font-normal">/mes</span></p>
                          <p className="text-xs text-slate-500 mt-1">Total: {formatPrice(p.total)}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Next Steps */}
                  <Card className="mb-8 text-left">
                    <CardContent className="pt-6">
                      <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                        <GraduationCap className="w-5 h-5 text-brand-blue" /> Siguientes Pasos
                      </h3>
                      <div className="space-y-3">
                        {result.next_steps?.map((step, i) => (
                          <div key={i} className="flex items-center gap-3">
                            <div className="w-7 h-7 bg-brand-blue/10 rounded-full flex items-center justify-center text-sm font-bold text-brand-blue flex-shrink-0">{i + 1}</div>
                            <span className="text-sm text-slate-700">{step}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <div className="flex items-center gap-2 text-xs text-slate-400 justify-center mb-6">
                    <Clock className="w-3 h-3" />
                    <span>Esta pre-aprobacion es valida por {result.valid_for_days} dias</span>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link to="/register">
                      <Button size="lg" className="bg-brand-magenta hover:bg-brand-magenta-dark text-white px-10 py-6 text-lg w-full" data-testid="register-after-approval">
                        Crear Cuenta y Aplicar <ArrowRight className="ml-2 w-5 h-5" />
                      </Button>
                    </Link>
                    <Link to="/programs">
                      <Button size="lg" variant="outline" className="border-brand-blue text-brand-blue px-10 py-6 text-lg w-full">
                        Ver Programas
                      </Button>
                    </Link>
                  </div>
                </div>
              ) : (
                /* REJECTED */
                <div className="text-center animate-fade-up">
                  <div className="w-20 h-20 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-6">
                    <XCircle className="w-10 h-10 text-white" />
                  </div>
                  <h2 className="text-3xl font-bold text-slate-900 mb-2" data-testid="rejection-result">
                    {result.stage === 'eligibility' ? 'No Elegible' : 'Pre-Aprobacion No Disponible'}
                  </h2>
                  <p className="text-slate-600 mb-6">{result.error}</p>

                  {result.demographics && (
                    <div className="grid grid-cols-3 gap-3 mb-6">
                      <div className="bg-white rounded-xl p-4 border text-center">
                        <p className="text-lg font-bold">{result.demographics.age} anos</p>
                        <p className="text-xs text-slate-500">Edad</p>
                      </div>
                      <div className="bg-white rounded-xl p-4 border text-center">
                        <p className="text-lg font-bold">{result.demographics.gender}</p>
                        <p className="text-xs text-slate-500">Genero</p>
                      </div>
                      <div className="bg-white rounded-xl p-4 border text-center">
                        <p className="text-sm font-bold">{result.demographics.state}</p>
                        <p className="text-xs text-slate-500">Estado</p>
                      </div>
                    </div>
                  )}

                  {result.suggestion && (
                    <Card className="mb-6 bg-amber-50 border-amber-200 text-left">
                      <CardContent className="pt-6">
                        <p className="text-sm text-amber-800"><strong>Sugerencia:</strong> {result.suggestion}</p>
                      </CardContent>
                    </Card>
                  )}

                  <Button onClick={() => { setResult(null); setCurp(''); }} variant="outline" className="border-brand-blue text-brand-blue" data-testid="try-again-btn">
                    Intentar de Nuevo
                  </Button>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Info Section (shown when no result) */}
        {!result && !loading && (
          <section className="py-16 bg-white">
            <div className="max-w-4xl mx-auto px-4 sm:px-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-center text-slate-900 mb-10">Como funciona?</h2>
              <div className="grid sm:grid-cols-3 gap-8">
                {[
                  { icon: <Shield className="w-8 h-8" />, title: "1. Ingresa tu CURP", desc: "Escribe tu Clave Unica de Registro de Poblacion (18 caracteres)" },
                  { icon: <Zap className="w-8 h-8" />, title: "2. Verificacion instantanea", desc: "Validamos tu identidad y consultamos tu historial crediticio en segundos" },
                  { icon: <DollarSign className="w-8 h-8" />, title: "3. Resultado inmediato", desc: "Conoce tu monto pre-aprobado y las opciones de pago disponibles" },
                ].map((step, i) => (
                  <div key={i} className="text-center">
                    <div className="w-16 h-16 bg-brand-blue/10 rounded-2xl flex items-center justify-center text-brand-blue mx-auto mb-4">{step.icon}</div>
                    <h3 className="font-bold text-lg mb-2">{step.title}</h3>
                    <p className="text-sm text-slate-600">{step.desc}</p>
                  </div>
                ))}
              </div>
              <div className="mt-10 bg-slate-50 rounded-2xl p-6 text-center">
                <p className="text-sm text-slate-500">
                  Tu CURP no se almacena. Solo usamos los primeros y ultimos 4 caracteres de forma anonima para estadisticas.
                  Sin compromiso, sin registro previo. Datos protegidos conforme a la LFPDPPP.
                </p>
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default PreApprovalPage;
